import { Receipt } from 'lucide-react'

import { simpleRoute } from '../../common/frontend/route-utils.js'
import { t } from '../../common/i18n.js'
import { EatMeal, EatOrder } from '../../common/types/api.js'
import { MenuItem, View } from '../types.js'
import { handleError } from '../utils/error-utils.js'
import {
    loadEatMealsIfNeeded,
    loadEatOrdersIfNeeded,
    loadEatSettingsIfNeeded,
} from '../utils/load-utils.js'
import {
    CartItem,
    CommentOption,
    EatViewProps,
    MealGroup,
    MealOption,
    OrderingSection,
    OrderPagination,
    OrderRow,
    PaymentSection,
    SummaryCard,
} from '../views/eat/eat.js'
import { EatView } from '../views/eat/eat.js'

const statusKeys = ['pending', 'ordered', 'paid', 'canceled'] as const

class EatModel {
    public route = simpleRoute<View>('/eat', {
        render: (view) => <EatView {...this.getProps(view)} />,
    })

    public menuItem: MenuItem = {
        text: t.eat.menuLabel,
        icon: Receipt,
        url: this.route.toUrl(),
        matchingRoutes: [this.route],
    }

    private getProps(view: View): EatViewProps {
        const { lang } = view.state
        const ordersState = loadEatOrdersIfNeeded(view)
        const mealsState = loadEatMealsIfNeeded(view)
        const settingsState = loadEatSettingsIfNeeded(view)

        const orders = ordersState.data ?? []
        const mealsData = mealsState.data
        const settings = settingsState.data
        const eatState = view.state.eat

        const isLoading = ordersState.isLoading && !ordersState.data

        // Summary calculations
        const unpaidOrders = orders.filter((o) => o.status !== 'paid' && o.status !== 'canceled')
        const unpaidTotal = unpaidOrders.reduce((sum, o) => sum + Number(o.price), 0)
        const paidOrders = orders.filter((o) => o.status === 'paid')

        const summaryCards: SummaryCard[] = []

        if (orders.length > 0) {
            summaryCards.push({
                label: t.eat.summary.unpaidCount(lang),
                value: String(unpaidOrders.length),
                variant: unpaidOrders.length > 0 ? 'warning' : 'success',
            })

            summaryCards.push({
                label: t.eat.summary.unpaidTotal(lang),
                value:
                    unpaidTotal > 0 ? `${unpaidTotal.toFixed(2)} €` : t.eat.summary.noUnpaid(lang),
                variant: unpaidTotal > 0 ? 'warning' : 'success',
            })

            if (unpaidOrders.length > 0) {
                const oldest = unpaidOrders[unpaidOrders.length - 1]
                summaryCards.push({
                    label: t.eat.summary.oldestUnpaid(lang),
                    value: oldest.created_at_utc.slice(0, 10),
                })
            }

            summaryCards.push({
                label: t.eat.summary.paidOrders(lang),
                value: String(paidOrders.length),
                variant: 'success',
            })
        }

        // Pagination
        const PAGE_SIZE = 10
        const totalPages = Math.max(1, Math.ceil(orders.length / PAGE_SIZE))
        const currentPage = Math.min(eatState.ordersPage, totalPages)
        const pageStart = (currentPage - 1) * PAGE_SIZE
        const pagedOrders = orders.slice(pageStart, pageStart + PAGE_SIZE)

        // Order rows
        const orderRows: OrderRow[] = pagedOrders.map((order: EatOrder) => {
            const statusKey = statusKeys.includes(order.status as (typeof statusKeys)[number])
                ? (order.status as (typeof statusKeys)[number])
                : 'pending'

            return {
                id: order.id,
                date: order.created_at_utc.slice(0, 10),
                meal: order.meal_name,
                comment: order.comment_text ?? '—',
                price: `${Number(order.price).toFixed(2)} €`,
                status: t.eat.orderStatuses[statusKey](lang),
                statusKey,
                canDelete: order.status === 'pending',
            }
        })

        // Build meal option helper
        const toMealOption = (m: EatMeal): MealOption => ({
            id: m.id,
            label: m.name,
            price: `${Number(m.price).toFixed(2)} €`,
        })

        const mealGroups: MealGroup[] = []

        if (mealsData) {
            if (mealsData.daily.length > 0) {
                mealGroups.push({
                    title: t.eat.ordering.dailyMenu(lang),
                    meals: mealsData.daily.map(toMealOption),
                })
            }
            if (mealsData.weekly.length > 0) {
                mealGroups.push({
                    title: t.eat.ordering.weeklyDeals(lang),
                    meals: mealsData.weekly.map(toMealOption),
                })
            }
            if (mealsData.regular.length > 0) {
                mealGroups.push({
                    title: t.eat.ordering.regularMenu(lang),
                    meals: mealsData.regular.map(toMealOption),
                })
            }
        }

        const commentOptions: CommentOption[] = (mealsData?.comments ?? []).map((c) => ({
            id: c.id,
            label: c.text,
            price: `${Number(c.price).toFixed(2)} €`,
        }))

        // Build cart display items
        const allMeals: EatMeal[] = mealsData
            ? [...mealsData.daily, ...mealsData.weekly, ...mealsData.regular]
            : []

        const cartItems: CartItem[] = eatState.cart.map((entry, index) => {
            const meal = allMeals.find((m) => m.id === entry.mealId)
            const comment = entry.commentId
                ? mealsData?.comments.find((c) => c.id === entry.commentId)
                : null
            const mealPrice = Number(meal?.price ?? 0)
            const commentPrice = Number(comment?.price ?? 0)
            return {
                index,
                mealLabel: meal?.name ?? String(entry.mealId),
                commentLabel: comment?.text ?? null,
                price: `${(mealPrice + commentPrice).toFixed(2)} €`,
            }
        })

        const ordering: OrderingSection = {
            title: t.eat.ordering.todayTitle(lang),
            isBlocked: mealsData?.isOrderingBlocked ?? false,
            blockedMessage: t.eat.ordering.orderBlocked(lang),
            noMealsMessage: t.eat.ordering.noMeals(lang),
            commentLabel: t.eat.ordering.comment(lang),
            noCommentLabel: t.eat.ordering.noComment(lang),
            addToCartLabel: t.eat.ordering.addToOrder(lang),
            placeOrderLabel: t.eat.ordering.placeOrder(lang),
            cartTitle: t.eat.ordering.cartTitle(lang),
            removeFromCartLabel: t.eat.ordering.removeFromCart(lang),
            isOrdering: eatState.isOrdering,
            mealGroups,
            comments: commentOptions,
            selectedMealId: eatState.selectedMealId,
            selectedCommentId: eatState.selectedCommentId,
            cart: cartItems,
            onSelectMeal: (id) => {
                eatState.selectedMealId = id
                view.update()
            },
            onSelectComment: (id) => {
                eatState.selectedCommentId = id
                view.update()
            },
            onAddToCart: () => this.handleAddToCart(view),
            onRemoveFromCart: (index) => this.handleRemoveFromCart(view, index),
            onPlaceOrder: () => this.handlePlaceOrder(view),
            onDeleteOrder: (id) => this.handleDeleteOrder(view, id),
        }

        // Show payment panel only when user has unpaid (ordered) orders
        const orderedOrders = orders.filter((o) => o.status === 'ordered')
        const payment: PaymentSection | null =
            orderedOrders.length > 0 && settings
                ? {
                      title: t.eat.ordering.paymentInfo(lang),
                      ibanLabel: t.eat.ordering.paymentIban(lang),
                      iban: settings.payment_iban,
                      payeeLabel: t.eat.ordering.paymentPayee(lang),
                      payee: settings.payment_payee,
                      markAsPaidLabel: t.eat.ordering.markAsPaid(lang),
                      isPaying: eatState.isPaying,
                      onPay: () => this.handlePay(view),
                  }
                : null

        const orderPagination: OrderPagination | null =
            totalPages > 1
                ? {
                      page: currentPage,
                      totalPages,
                      onPrev: () => {
                          eatState.ordersPage = Math.max(1, currentPage - 1)
                          view.update()
                      },
                      onNext: () => {
                          eatState.ordersPage = Math.min(totalPages, currentPage + 1)
                          view.update()
                      },
                  }
                : null

        return {
            lang,
            title: t.eat.summary.title(lang),
            isLoading,
            summaryCards,
            ordersTitle: t.eat.summary.myOrders(lang),
            orderRows,
            orderPagination,
            emptyMessage: t.eat.summary.noOrders(lang),
            ordering,
            payment,
        }
    }

    private handleAddToCart(view: View) {
        const { state, update } = view
        const { selectedMealId, selectedCommentId } = state.eat

        if (selectedMealId === null) return

        state.eat.cart.push({ mealId: selectedMealId, commentId: selectedCommentId })
        state.eat.selectedMealId = null
        state.eat.selectedCommentId = null
        update()
    }

    private handleRemoveFromCart(view: View, index: number) {
        view.state.eat.cart.splice(index, 1)
        view.update()
    }

    private handlePlaceOrder(view: View) {
        const { state, api, update } = view
        const { cart } = state.eat

        if (cart.length === 0 || state.eat.isOrdering) return

        state.eat.isOrdering = true
        update()

        Promise.all(cart.map((item) => api.eat.createOrder(item.mealId, item.commentId)))
            .then(() => {
                state.eat.cart = []
                state.eat.orders.data = undefined
                state.eat.orders.isLoading = false
                state.eat.meals.data = undefined
                state.eat.meals.isLoading = false
            })
            .catch(() => {
                // Error already handled by httpSend
            })
            .finally(() => {
                state.eat.isOrdering = false
                update()
            })
    }

    private handleDeleteOrder(view: View, orderId: number) {
        const { state, api, update } = view

        api.eat
            .deleteOrder(orderId)
            .then(() => {
                state.eat.orders.data = undefined
                state.eat.orders.isLoading = false
                update()
            })
            .catch((error: unknown) => {
                handleError(view, error)
            })
    }

    private handlePay(view: View) {
        const { state, api, update } = view
        const userId = state.user?.id

        if (!userId || state.eat.isPaying) return

        state.eat.isPaying = true
        update()

        api.eat
            .pay(userId)
            .then(() => {
                state.eat.orders.data = undefined
                state.eat.orders.isLoading = false
            })
            .catch(() => {
                // Error already handled
            })
            .finally(() => {
                state.eat.isPaying = false
                update()
            })
    }
}

export const eatModel = new EatModel()
